// src/components/charts/ProgressRing.tsx
//
// ★★★ WAVE 27 — ONE RATIO, and the argument for why it is a chart at all.
//
// ⚠ **`choosing-a-form.md` SAYS THE ANSWER IS OFTEN "NOT A CHART".** A single
// percentage is usually a stat tile: a number, large, with its basis under it. A
// ring adds nothing to `62%` except decoration, and decoration on a government
// operations screen is a cost.
//
// The ring earns its place on exactly one condition, which this component enforces
// by requiring both figures: **the reader needs the PART and the WHOLE at once.**
// "3,527 of 5,150 approved" is two facts and a relationship; the arc carries the
// relationship, the centre carries the part, the caption carries the whole. A bar
// would do the same job — the ring is chosen because it sits in a card beside a
// donut and a heatmap without imposing a horizontal axis nothing else on the row
// has.
//
// ⚠ **IT IS NOT A GAUGE, and the difference matters.** A gauge implies a target —
// a red zone, a needle, "good" and "bad". Nothing here is a target: the share of
// bills approved is an outcome of casework, not a KPI anybody is measured against,
// and dressing it as one would invent a judgement the tender never asked for. No
// zones, no colours-as-verdict; the arc wears a series hue, not a status hue.
//
// ⚠ **STATUS COLOURS ARE RESERVED** (`dataviz`, non-negotiables) — good/warning/
// critical never get reused as "series 4". That is why `slot` is a categorical
// slot and there is no way to pass a status colour in.
import type { ReactElement } from 'react'
import { MarkNoteBubble, MarkNoteLayer, markProps, useMarkNote } from './mark-interaction'

const SIZE = 168
const STROKE = 14
const RADIUS = (SIZE - STROKE) / 2
const CIRCUMFERENCE = 2 * Math.PI * RADIUS

export function ProgressRing({
  label,
  part,
  whole,
  caption,
  slot = 1,
  partLabel,
  onSelectPart,
  remainderLabel,
  onSelectRemainder,
}: {
  readonly label: string
  readonly part: number
  readonly whole: number
  /** What the ratio is OF, in words. Rendered under the number. */
  readonly caption: string
  readonly slot?: 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8
  /**
   * ★★★ What the FILLED arc is, and where pressing it goes.
   *
   * ⚠ Both halves of the ring are named and both are pressable, because a
   * reader looking at "69% approved" wants the other 31% at least as often — and
   * a ring where only the coloured part responds teaches them the grey part is
   * decoration. Omit the callbacks and the ring is inert, which is right for a
   * ratio with no list behind it.
   */
  readonly partLabel?: string
  readonly onSelectPart?: () => void
  /** What the UNFILLED arc is — everything the part is not. */
  readonly remainderLabel?: string
  readonly onSelectRemainder?: () => void
}): ReactElement {
  const fraction = whole <= 0 ? 0 : Math.min(1, part / whole)
  const percent = whole <= 0 ? null : Math.round(fraction * 100)
  const remainder = Math.max(0, whole - part)
  const { note, show, hide } = useMarkNote()

  return (
    <figure className="pt-chart-figure">
      <figcaption>
        <h3 className="text-lg">{label}</h3>
      </figcaption>
      <MarkNoteLayer>
      <div className="pt-chart-plot flex flex-col items-center">
        <svg viewBox={`0 0 ${SIZE} ${SIZE}`} className="pt-ring" role="img" aria-label={`${label}: ${caption}`}>
          {/*
            ⚠ THE TRACK IS A MARK TOO. It is everything the filled arc is not, and
            a reader who points at it should be told that rather than left to infer
            it from an absence.
          */}
          <circle
            cx={SIZE / 2}
            cy={SIZE / 2}
            r={RADIUS}
            strokeWidth={STROKE}
            {...markProps({
              className: 'pt-ring-track',
              note: {
                label: remainderLabel ?? 'The rest',
                value: `${remainder.toLocaleString('en-SG')} of ${whole.toLocaleString('en-SG')}`,
              },
              ...(onSelectRemainder === undefined ? {} : { onSelect: onSelectRemainder }),
              show,
              hide,
            })}
          />
          {/*
            ⚠ Rotated so the arc starts at twelve o'clock. A ring that begins at
            three reads as a different quantity to everyone who has seen any other
            ring.
          */}
          <circle
            cx={SIZE / 2}
            cy={SIZE / 2}
            r={RADIUS}
            strokeWidth={STROKE}
            strokeDasharray={`${(fraction * CIRCUMFERENCE).toFixed(2)} ${CIRCUMFERENCE.toFixed(2)}`}
            transform={`rotate(-90 ${SIZE / 2} ${SIZE / 2})`}
            {...markProps({
              className: `pt-ring-value pt-series-stroke-${slot}`,
              note: {
                label: partLabel ?? label,
                value: `${part.toLocaleString('en-SG')} of ${whole.toLocaleString('en-SG')}`,
              },
              ...(onSelectPart === undefined ? {} : { onSelect: onSelectPart }),
              show,
              hide,
            })}
          />
          <text className="pt-ring-figure" x={SIZE / 2} y={SIZE / 2 + 2} textAnchor="middle">
            {percent === null ? 'None' : `${percent}%`}
          </text>
        </svg>
        {/*
          ⚠ BOTH FIGURES IN WORDS, under the ring. The percentage alone is the
          half-truth this form is prone to: 62% of eight bills and 62% of eight
          thousand are not the same fact, and only one of them is worth acting on.
        */}
        <p className="text-sm text-[var(--sgds-body-color-subtle)] text-center mt-2">
          <strong className="pt-tabular">{part.toLocaleString('en-SG')}</strong> of{' '}
          <strong className="pt-tabular">{whole.toLocaleString('en-SG')}</strong> {caption}
        </p>
      </div>
      <MarkNoteBubble note={note} />
      </MarkNoteLayer>
    </figure>
  )
}
